import {
  AlertTriangleIcon,
  BookOpenIcon,
  CircleHelpIcon,
  BriefcaseBusinessIcon,
  BuildingIcon,
  CalendarIcon,
  ClipboardListIcon,
  FilesIcon,
  GraduationCapIcon,
  LayoutDashboardIcon,
  TagsIcon,
  TimerIcon,
  UsersIcon,
} from 'lucide-react'
import { createBrowserRouter, Navigate } from 'react-router'

import EntityRedirect from '@/components/EntityRedirect'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PrivateRoute } from '@/components/PrivateRoute'
import { PublicRoute } from '@/components/PublicRoute'
// Account (Entity) Pages
import Demo from '@/components/Quiz/Demo'
import { AccountDetails } from '@/pages/Accounts/AccountDetails'
import { AccountManagement } from '@/pages/Accounts/AccountManagement'
import { Calendar as AccountsCalendar } from '@/pages/Accounts/Calendar'
import { CertificationCategories as AccountsCertificationCategories } from '@/pages/Accounts/CertificationCategories'
import {
  Courses as AccountsCourses,
  CourseForm as AccountsCourseForm,
} from '@/pages/Accounts/Courses'
import { CreditBank } from '@/pages/Accounts/CreditBank'
import { Documents as AccountsDocuments } from '@/pages/Accounts/Documents'
import { EAPs, EAPQuiz, EAPCompliance } from '@/pages/Accounts/EAPs'
import { EAPForm } from '@/pages/Accounts/EAPs/EAPForm'
import { EAPPreview } from '@/pages/Accounts/EAPs/EAPPreview'
import { Faqs as AccountsFaqs } from '@/pages/Accounts/Faqs'
import {
  Certificates as InstructorsCertificates,
  Certificate as InstructorsCertificate,
} from '@/pages/Accounts/Instructors/Certificates'
import {
  Students as InstructorsStudents,
  Student as InstructorsStudent,
} from '@/pages/Accounts/Instructors/Students'
import { Jobs as AccountsJobs } from '@/pages/Accounts/Jobs'
import { Users as AccountsUsers } from '@/pages/Accounts/Users'
import { Profile as AccountsUserProfile } from '@/pages/Accounts/Users/Profile'
// Admin Pages
import { AddRecoveryMethod } from '@/pages/AddRecoveryMethod'
import { Accounts as AdminAccounts } from '@/pages/Admin/Accounts'
import { AccountsHub } from '@/pages/Admin/AccountsHub'
import { Certificate as AdminCertificate } from '@/pages/Admin/Certificate'
import { Certificates as AdminCertificates } from '@/pages/Admin/Certificates'
import { Course as AdminCourse } from '@/pages/Admin/Course'
import { Courses as AdminCourses } from '@/pages/Admin/Courses'
import { ExternalCertificationCategories as AdminExternalCertificationCategories } from '@/pages/Admin/ExternalCertificationCategories'
import { Organizations as AdminOrganizations } from '@/pages/Admin/Organizations'
import { Trainings as AdminTrainings } from '@/pages/Admin/Trainings'
import { Users as AdminUsers } from '@/pages/Admin/Users'
// Other Pages
import { CompleteProfile } from '@/pages/CompleteProfile'
import { CourseAssets } from '@/pages/CourseAssets'
import { CoursesHub } from '@/pages/CoursesHub'
import { Dashboard } from '@/pages/Dashboard'
import { ErrorPage } from '@/pages/Error'
import { PrivacyPolicy, TermsOfUse, WaiverAndRelease } from '@/pages/Legal'
import { Login } from '@/pages/Login'
import { MultiFactorAuthentication } from '@/pages/MultiFactorAuthentication'
import { OrganizationAccounts } from '@/pages/Organizations/OrganizationAccounts'
import { OrganizationDashboard } from '@/pages/Organizations/OrganizationDashboard'
import { OrganizationEAPTemplates } from '@/pages/Organizations/OrganizationEAPTemplates'
import { OrganizationJobs } from '@/pages/Organizations/OrganizationJobs'
import { OrganizationUsers } from '@/pages/Organizations/OrganizationUsers'
import { PasswordReset } from '@/pages/PasswordReset'
import { PasswordResetRequest } from '@/pages/PasswordResetRequest'
import { UserCertificates } from '@/pages/UserCertificates'
import { UserProfile } from '@/pages/UserProfile'

// Drill Timer Pages
import { Documents } from './pages/Admin/Documents'
import { Faqs as AdminFaqs } from './pages/Admin/Faqs'
import { DrillActiveScreen } from './pages/DrillTimer/DrillActiveScreen'
import { DrillLogDetailScreen } from './pages/DrillTimer/DrillLogDetailScreen'
import { DrillLogsListScreen } from './pages/DrillTimer/DrillLogsListScreen'
import { DrillReportScreen } from './pages/DrillTimer/DrillReportScreen'
import { DrillSetupScreen } from './pages/DrillTimer/DrillSetupScreen'
import { DrillTemplateFormScreen } from './pages/DrillTimer/DrillTemplateFormScreen'
import { DrillTemplatesListScreen } from './pages/DrillTimer/DrillTemplatesListScreen'
import { StudentCourse } from './pages/Students/Course'
import { StudentCourses } from './pages/Students/Courses'

/**
 * Shared route object rules
 */
const sharedRouteObjectRules = {
  errorElement: <ErrorBoundary />,
}

/**
 * Public Routes (inheritable)
 *
 * These routes are accessible to anyone
 */
const PublicRoutes = {
  id: 'PublicRoutes',
  Component: PublicRoute,
  children: [
    {
      index: true,
      Component: Login,
    },
    {
      path: '/login',
      Component: Login,
      caseSensitive: true,
    },
    {
      path: '/verification',
      Component: MultiFactorAuthentication,
      caseSensitive: true,
    },
    {
      path: '/password-reset',
      Component: PasswordReset,
      caseSensitive: true,
    },
    {
      path: '/set-password',
      Component: PasswordReset,
      caseSensitive: true,
    },
    {
      path: '/password-reset-request',
      Component: PasswordResetRequest,
      caseSensitive: true,
    },
  ],
  ...sharedRouteObjectRules,
}

/**
 * Private Routes (inheritable)
 *
 * These routes are accessible only to authenticated users. Meant to be inherited by
 * the objects that define protected routes.
 */
const PrivateRoutesBase = { Component: PrivateRoute, ...sharedRouteObjectRules }

/**
 * Admin Routes
 *
 * These routes are accessible only to authenticated users with the "Xverse Admin" role,
 * e.g. {isXverseAdmin: true}.
 */
const AdminRoutes = {
  id: 'AdminRoutes',
  children: [
    // Index redirect
    {
      path: '/admin',
      children: [
        {
          index: true,
          element: <Navigate replace to="accounts" />,
        },
        // Legacy list URLs → redirect into the new hub.
        {
          path: 'certificates',
          element: <Navigate replace to="/admin/courses/courses" />,
        },
        {
          path: 'course-assets',
          element: <Navigate replace to="/admin/courses/course-assets" />,
        },
      ],
    },
    // Top-level admin nav items
    // Accounts hub — one nav entry, two tabs (Accounts / Organizations).
    // RSCX-320: Organizations are managed here, alongside the accounts they
    // parent. Mirrors the /admin/courses hub.
    {
      path: '/admin/accounts',
      Component: AccountsHub,
      handle: {
        title: 'Accounts',
        icon: BuildingIcon,
        showInNav: true,
      },
      children: [
        // Accounts is the hub's landing tab — rendered at /admin/accounts
        // itself (no doubled /admin/accounts/accounts). Organizations is a
        // sibling tab. RSCX-320.
        { index: true, Component: AdminAccounts },
        {
          path: 'organizations',
          Component: AdminOrganizations,
          caseSensitive: true,
        },
      ],
    },
    // Unified "Courses" hub — one nav entry, three tabs (Courses /
    // Course Modules / Course Assets). Editors stay as nested child
    // routes; the hub renders just <Outlet /> for those.
    {
      path: '/admin/courses',
      Component: CoursesHub,
      caseSensitive: true,
      handle: {
        title: 'Courses',
        icon: BookOpenIcon,
        showInNav: true,
      },
      children: [
        { index: true, element: <Navigate replace to="courses" /> },
        {
          path: 'courses',
          Component: AdminCertificates,
          caseSensitive: true,
        },
        {
          path: 'courses/:certificateId',
          Component: AdminCertificate,
          caseSensitive: true,
          handle: { title: 'Course' },
        },
        {
          path: 'course-modules',
          Component: AdminCourses,
          caseSensitive: true,
        },
        {
          path: 'course-modules/:courseId',
          Component: AdminCourse,
          caseSensitive: true,
          handle: { title: 'Course Module' },
        },
        {
          path: 'course-assets',
          Component: CourseAssets,
          caseSensitive: true,
        },
      ],
    },
    {
      path: '/admin/trainings',
      Component: AdminTrainings,
      caseSensitive: true,
      handle: {
        title: 'Trainings',
        icon: ClipboardListIcon,
        showInNav: true,
      },
    },
    {
      path: '/admin/users',
      Component: AdminUsers,
      caseSensitive: true,
      handle: {
        title: 'Users',
        icon: UsersIcon,
        showInNav: true,
      },
    },
    {
      path: '/admin/documents',
      Component: Documents,
      caseSensitive: true,
      handle: {
        title: 'Documents',
        icon: FilesIcon,
        showInNav: true,
      },
    },
    {
      path: '/admin/faqs',
      Component: AdminFaqs,
      caseSensitive: true,
      handle: {
        title: 'FAQs',
        icon: CircleHelpIcon,
        showInNav: true,
      },
    },
    {
      path: '/admin/certification-categories',
      Component: AdminExternalCertificationCategories,
      caseSensitive: true,
      handle: {
        title: 'External Credential Categories',
        icon: TagsIcon,
        showInNav: true,
      },
    },
  ],
  ...PrivateRoutesBase,
}

/**
 * Entity Routes
 *
 * These routes are accessible only to authenticated users with the following roles:
 * "Owner", "Admin", or "Member" for a given entity.
 *
 * @todo: Should these be permissible to Xverse Admins too?
 */
const EntityRoutes = {
  id: 'EntityRoutes',
  children: [
    // Accounts landing (lets PrivateRoute handle the redirect)
    {
      path: '/accounts',
      children: [{ index: true }],
    },
    // Dashboard (all roles)
    {
      path: '/accounts/:entityId/dashboard',
      Component: Dashboard,
      handle: {
        title: 'Dashboard',
        icon: LayoutDashboardIcon,
        roles: ['Admin', 'Owner', 'Instructor', 'Member'],
        showInNav: true,
        entityScoped: true,
      },
    },
    // User profile (all roles) — kept outside the Admin group because it's
    // viewable by Instructors and Members too. Living under '/users/:roleId'
    // would otherwise inherit the admin section's structural roles even
    // though PrivateRoute only enforces the leaf handle.
    {
      path: '/accounts/:entityId/users/:roleId',
      Component: AccountsUserProfile,
      handle: {
        title: 'Profile',
        roles: ['Admin', 'Owner', 'Instructor', 'Member'],
        showInNav: false,
        entityScoped: true,
      },
    },
    // Admins
    {
      path: '/accounts/:entityId',
      handle: {
        title: 'Admin',
        icon: UsersIcon,
        group: true,
        roles: ['Admin', 'Owner'],
        showInNav: true,
        entityScoped: true,
      },
      children: [
        {
          index: true,
          element: <EntityRedirect />,
        },
        {
          path: 'account-management',
          Component: AccountManagement,
          handle: {
            title: 'Account Management',
            icon: BuildingIcon,
            roles: ['Admin', 'Owner'],
            showInNav: true,
            entityScoped: true,
          },
          children: [
            {
              index: true,
              element: <Navigate replace to="details" />,
            },
            {
              path: 'details',
              Component: AccountDetails,
              caseSensitive: true,
              handle: {
                roles: ['Admin', 'Owner'],
                entityScoped: true,
              },
            },
            {
              path: 'jobs',
              Component: AccountsJobs,
              handle: {
                roles: ['Admin', 'Owner'],
                entityScoped: true,
              },
            },
            {
              path: 'credit-bank',
              Component: CreditBank,
              caseSensitive: true,
              handle: {
                roles: ['Admin', 'Owner'],
                entityScoped: true,
              },
            },
          ],
        },
        {
          path: 'users',
          Component: AccountsUsers,
          handle: {
            title: 'Users',
            roles: ['Admin', 'Owner'],
            showInNav: true,
            entityScoped: true,
          },
        },
        // Redirect old URLs to new structure
        {
          path: 'details',
          element: <Navigate replace to="account-management/details" />,
        },
        {
          path: 'jobs',
          element: <Navigate replace to="account-management/jobs" />,
        },
        {
          path: 'credit-bank',
          element: <Navigate replace to="account-management/credit-bank" />,
        },
      ],
    },

    // Instructors
    {
      // NOTE: we append instructor to avoid conflicts with other routes
      path: '/accounts/:entityId/instructors',
      handle: {
        title: 'Instructor',
        icon: BriefcaseBusinessIcon,
        group: true,
        roles: ['Instructor'],
        showInNav: true,
      },
      children: [
        {
          index: true,
          element: <Navigate replace to="certificates" />,
        },
        {
          path: 'certificates',
          Component: InstructorsCertificates,
          caseSensitive: true,
          handle: {
            title: 'Course Assignments',
            roles: ['Instructor'],
            showInNav: true,
          },
        },
        {
          path: 'certificates/:certificateId',
          Component: InstructorsCertificate,
          caseSensitive: true,
          handle: {
            title: 'Course',
            roles: ['Instructor'],
            showInNav: false,
          },
        },
        {
          path: 'students',
          Component: InstructorsStudents,
          handle: {
            title: 'Students',
            roles: ['Instructor'],
            showInNav: true,
          },
        },
        {
          path: 'students/:studentId',
          Component: InstructorsStudent,
          handle: {
            title: 'Student',
            roles: ['Instructor'],
            showInNav: false,
          },
        },
        // Facility courses hub — Instructor group child (RSCX-196: "if you
        // can teach a course, you can edit it" → Instructor gets visibility
        // + edit rights, Owner / Admin cascade in via userHasAccess). Same
        // shape as the admin hub — three tabs for Certificate, Course, and
        // CourseAsset. Nested under the Instructor route so it appears as
        // an item inside that nav group instead of a sibling.
        {
          path: 'courses',
          Component: CoursesHub,
          handle: {
            title: 'Custom Courses',
            roles: ['Instructor'],
            // Account-level subscription gate — the hub and all its children
            // are hidden + blocked unless the active entity has this switch on.
            // Gated server-side at the model only for who-can-toggle; feature
            // access itself is enforced here on the frontend (per RSCX-13).
            access: 'accessCustomCourses',
            showInNav: true,
            entityScoped: true,
          },
          children: [
            { index: true, element: <Navigate replace to="courses" /> },
            {
              path: 'courses',
              Component: AdminCertificates,
              handle: {
                roles: ['Instructor'],
                entityScoped: true,
              },
            },
            {
              path: 'courses/:certificateId',
              Component: AdminCertificate,
              handle: {
                title: 'Edit Course',
                roles: ['Instructor'],
                entityScoped: true,
              },
            },
            {
              path: 'course-modules',
              Component: AccountsCourses,
              handle: {
                roles: ['Instructor'],
                entityScoped: true,
              },
            },
            {
              path: 'course-modules/:courseId',
              Component: AccountsCourseForm,
              handle: {
                title: 'Edit Course Module',
                roles: ['Instructor'],
                entityScoped: true,
              },
            },
            {
              path: 'course-assets',
              Component: CourseAssets,
              handle: {
                roles: ['Instructor'],
                entityScoped: true,
              },
            },
          ],
        },
      ],
    },
    {
      path: '/accounts/:entityId/students/courses/',
      Component: StudentCourses,
      handle: {
        title: 'My Courses',
        icon: GraduationCapIcon,
        group: true,
        roles: ['Member'],
        showInNav: true,
      },
    },
    {
      path: '/accounts/:entityId/students/courses/:courseId',
      Component: StudentCourse,
      handle: {
        group: true,
        roles: ['Member'],
        showInNav: false,
      },
    },
    // Legacy URLs → redirect into the new hub path so bookmarks survive.
    {
      path: '/accounts/:entityId/courses',
      element: <Navigate replace to="../instructors/courses" relative="path" />,
    },
    {
      path: '/accounts/:entityId/courses/*',
      element: <Navigate replace to="../instructors/courses" relative="path" />,
    },
    {
      path: '/accounts/:entityId/course-assets',
      element: <Navigate replace to="../instructors/courses/course-assets" relative="path" />,
    },

    // All roles
    {
      path: '/accounts/:entityId/eaps',
      Component: EAPs,
      handle: {
        title: 'Emergency Action Plans',
        icon: AlertTriangleIcon,
        roles: ['Admin', 'Owner', 'Instructor', 'Member'],
        showInNav: true,
        entityScoped: true,
      },
    },
    {
      path: '/accounts/:entityId/eaps/:eapId',
      Component: EAPForm,
      handle: {
        title: 'Edit Emergency Action Plan',
        roles: ['Admin', 'Owner'],
        showInNav: false,
        entityScoped: true,
      },
    },
    {
      path: '/accounts/:entityId/eaps/:eapId/preview',
      Component: EAPPreview,
      handle: {
        title: 'Preview Emergency Action Plan',
        roles: ['Admin', 'Owner', 'Instructor', 'Member'],
        showInNav: false,
        entityScoped: true,
      },
    },
    {
      path: '/accounts/:entityId/eaps/:eapId/quiz',
      Component: EAPQuiz,
      handle: {
        title: 'EAP Quiz',
        roles: ['Admin', 'Owner', 'Instructor', 'Member'],
        showInNav: false,
        entityScoped: true,
      },
    },
    {
      path: '/accounts/:entityId/eaps/:eapId/compliance',
      Component: EAPCompliance,
      handle: {
        title: 'EAP Compliance',
        roles: ['Admin', 'Owner'],
        showInNav: false,
        entityScoped: true,
      },
    },
    {
      path: '/accounts/:entityId/drill-timer',
      handle: {
        title: 'Drill Timer',
        icon: TimerIcon,
        roles: ['Admin', 'Owner'],
        showInNav: true,
        entityScoped: true,
      },
      children: [
        {
          index: true,
          Component: DrillLogsListScreen,
        },
        {
          path: 'setup',
          Component: DrillSetupScreen,
          caseSensitive: true,
          handle: {
            title: 'Start New Drill',
            roles: ['Admin', 'Owner', 'Instructor'],
            showInNav: false,
            entityScoped: true,
          },
        },
        {
          path: 'active',
          Component: DrillActiveScreen,
          caseSensitive: true,
          handle: {
            title: 'Active Drill',
            roles: ['Admin', 'Owner', 'Instructor'],
            showInNav: false,
            entityScoped: true,
          },
        },
        {
          path: 'report',
          Component: DrillReportScreen,
          caseSensitive: true,
          handle: {
            title: 'Drill Report',
            roles: ['Admin', 'Owner', 'Instructor'],
            showInNav: false,
            entityScoped: true,
          },
        },
        {
          path: 'logs/:logId',
          Component: DrillLogDetailScreen,
          caseSensitive: true,
          handle: {
            title: 'Drill Log Detail',
            roles: ['Admin', 'Owner', 'Instructor'],
            showInNav: false,
            entityScoped: true,
          },
        },
        {
          path: 'templates',
          Component: DrillTemplatesListScreen,
          caseSensitive: true,
          handle: {
            title: 'Manage Templates',
            roles: ['Admin', 'Owner'],
            showInNav: false,
            entityScoped: true,
          },
        },
        {
          path: 'templates/new',
          Component: DrillTemplateFormScreen,
          caseSensitive: true,
          handle: {
            title: 'New Template',
            roles: ['Admin', 'Owner'],
            showInNav: false,
            entityScoped: true,
          },
        },
        {
          path: 'templates/:templateId/edit',
          Component: DrillTemplateFormScreen,
          caseSensitive: true,
          handle: {
            title: 'Edit Template',
            roles: ['Admin', 'Owner'],
            showInNav: false,
            entityScoped: true,
          },
        },
      ],
    },
    {
      path: '/accounts/:entityId/calendar',
      Component: AccountsCalendar,
      caseSensitive: true,
      handle: {
        title: 'Calendar',
        icon: CalendarIcon,
        roles: ['Admin', 'Owner', 'Instructor', 'Member', 'Student'],
        showInNav: true,
        entityScoped: true,
      },
    },
    {
      path: '/accounts/:entityId/documents',
      handle: {
        title: 'Documents',
        icon: FilesIcon,
        roles: ['Admin', 'Owner', 'Instructor', 'Member', 'Student'],
        showInNav: true,
        entityScoped: true,
      },
      children: [
        {
          index: true,
          Component: AccountsDocuments,
        },
        {
          path: 'certification-categories',
          Component: AccountsCertificationCategories,
          caseSensitive: true,
          handle: {
            title: 'External Credential Categories',
            roles: ['Admin', 'Owner'],
            showInNav: false,
            entityScoped: true,
          },
        },
      ],
    },
    {
      path: '/accounts/:entityId/faqs',
      Component: AccountsFaqs,
      caseSensitive: true,
      handle: {
        title: 'FAQs',
        icon: CircleHelpIcon,
        roles: ['Admin', 'Owner', 'Instructor', 'Member', 'Student'],
        showInNav: true,
        entityScoped: true,
        // Hidden until RescueX has published at least one FAQ; the sidebar
        // supplies hasPublishedFaqs (see getNavItems).
        requiresPublishedFaqs: true,
      },
    },
  ],
  ...PrivateRoutesBase,
}

/**
 * Organization Routes (RSCX-320)
 *
 * Parent-account context. Accessible to Org Owners/Admins of the URL's
 * organization (and Xverse Admins); PrivateRoute enforces org membership rather
 * than entity roles, so these routes intentionally carry no entity `roles`.
 */
const OrganizationRoutes = {
  id: 'OrganizationRoutes',
  children: [
    {
      path: '/organizations/:organizationId/dashboard',
      Component: OrganizationDashboard,
      handle: {
        title: 'Dashboard',
        icon: LayoutDashboardIcon,
        showInNav: true,
        orgScoped: true,
      },
    },
    {
      path: '/organizations/:organizationId/accounts',
      Component: OrganizationAccounts,
      handle: {
        // Org-facing label: an org's child accounts are "Facilities" (RSCX-320).
        title: 'Facilities',
        icon: BuildingIcon,
        showInNav: true,
        orgScoped: true,
      },
    },
    {
      path: '/organizations/:organizationId/users',
      Component: OrganizationUsers,
      handle: {
        title: 'Users',
        icon: UsersIcon,
        showInNav: true,
        orgScoped: true,
      },
    },
    {
      path: '/organizations/:organizationId/jobs',
      Component: OrganizationJobs,
      handle: {
        title: 'Jobs',
        icon: BriefcaseBusinessIcon,
        showInNav: true,
        orgScoped: true,
      },
    },
    {
      path: '/organizations/:organizationId/eap-templates',
      Component: OrganizationEAPTemplates,
      handle: {
        title: 'EAPs',
        icon: ClipboardListIcon,
        showInNav: true,
        orgScoped: true,
      },
    },
    {
      // Reuses the facility EAPForm in "shared" mode (detected from the
      // organizationId param). Not in nav — reached from the list.
      path: '/organizations/:organizationId/eap-templates/:eapId',
      Component: EAPForm,
      handle: {
        title: 'EAP',
        orgScoped: true,
      },
    },
    {
      // Org Custom Courses hub (RSCX-320) — full parity with the facility hub,
      // reusing the same CoursesHub + child editors in "org" scope (detected
      // from the organizationId param). Everything authored here is shared
      // read-only with the org's facilities. Same three tabs: Custom Courses
      // (certificates), Course Modules, Course Assets.
      path: '/organizations/:organizationId/courses',
      Component: CoursesHub,
      handle: {
        title: 'Custom Courses',
        icon: GraduationCapIcon,
        showInNav: true,
        orgScoped: true,
      },
      children: [
        { index: true, element: <Navigate replace to="courses" /> },
        {
          path: 'courses',
          Component: AdminCertificates,
          handle: { orgScoped: true },
        },
        {
          path: 'courses/:certificateId',
          Component: AdminCertificate,
          handle: { title: 'Edit Course', orgScoped: true },
        },
        {
          path: 'course-modules',
          Component: AccountsCourses,
          handle: { orgScoped: true },
        },
        {
          path: 'course-modules/:courseId',
          Component: AccountsCourseForm,
          handle: { title: 'Edit Course Module', orgScoped: true },
        },
        {
          path: 'course-assets',
          Component: CourseAssets,
          handle: { orgScoped: true },
        },
      ],
    },
  ],
  ...PrivateRoutesBase,
}

/**
 * General Routes
 *
 * These routes are accessible to any authenticated user.
 */
const GeneralRoutes = {
  id: 'GeneralRoutes',
  children: [
    {
      path: '/complete-profile',
      Component: CompleteProfile,
      caseSensitive: true,
    },
    {
      path: '/add-recovery-method',
      Component: AddRecoveryMethod,
      caseSensitive: true,
    },
    {
      path: '/profile',
      Component: UserProfile,
      caseSensitive: true,
    },
    {
      path: '/certificates',
      Component: UserCertificates,
      caseSensitive: true,
    },
  ],
  ...PrivateRoutesBase,
}

/**
 * Router
 */
export const router = createBrowserRouter([
  PublicRoutes,
  AdminRoutes,
  EntityRoutes,
  OrganizationRoutes,
  GeneralRoutes,

  // Other
  {
    children: [
      {
        path: '/terms-of-use',
        Component: TermsOfUse,
        caseSensitive: true,
      },
      {
        path: '/privacy-policy',
        Component: PrivacyPolicy,
        caseSensitive: true,
      },
      {
        path: '/waiver-and-release',
        Component: WaiverAndRelease,
        caseSensitive: true,
      },
    ],
    errorElement: <ErrorBoundary />,
  },
  {
    path: 'quiz-demo',
    Component: Demo,
  },

  // 404 fallback
  {
    path: '*',
    Component: ErrorPage,
  },
])
